// utils/emailTemplateColumns.ts

const STORAGE_KEY = "email_template_visible_columns";

const toPlaceholderKey = (raw: any): string => {
  let key = "";
  if (Array.isArray(raw)) {
    key = raw.filter(Boolean).join(".");
  } else if (raw?.props?.children) {
    key = extractTextFromNode(raw.props.children);
  } else {
    key = String(raw || "").trim();
  }
  if (!key) return "";
  return key
    .replace(/[-_\s]+(.)?/g, (_, c) => (c ? c.toUpperCase() : ""))
    .replace(/^(.)/, (_, c) => c.toLowerCase())
    .replace(/[^a-zA-Z0-9]/g, "");
};

const extractTextFromNode = (node: any): string => {
  if (!node) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractTextFromNode).join(" ");
  if (node.props?.children) return extractTextFromNode(node.props.children);
  return "";
};

const flattenColumns = (columns: any[]): any[] => {
  const result: any[] = [];
  for (const col of columns) {
    if (!col) continue;
    result.push(col);
    if (Array.isArray(col.children)) {
      result.push(...flattenColumns(col.children));
    }
  }
  return result;
};

const getColumnLabel = (col: any): string => {
  const title = col.title || col.headerName || col.label;
  if (typeof title === "string") return title;
  if (typeof title === "number") return String(title);
  if (Array.isArray(title))
    return title.map(getColumnLabel).filter(Boolean).join(" ");
  if (title?.props?.children) return getColumnLabel(title.props.children);
  return "";
};

export const registerModelColumns = (
  modelName: string,
  columns: any[],
): void => {
  if (typeof window === "undefined") return;
  if (!modelName || !Array.isArray(columns)) return;

  const flattened = flattenColumns(columns);
  const visibleColumns: { key: string; label: string }[] = [];
  const seen = new Set<string>();

  const skipLabels = new Set([
    "action",
    "#",
    "s.no",
    "sr.no",
    "s no",
    "serial",
    "serial no",
  ]);
  const skipKeys = new Set(["action", "_id", "id"]);

  for (const col of flattened) {
    if (
      col.hidden === true ||
      col.isHidden === true ||
      col.visible === false ||
      col.show === false
    ) {
      continue;
    }

    const label = getColumnLabel(col);
    const labelLower = label.toLowerCase().trim();

    if (skipLabels.has(labelLower)) continue;

    // FALLBACK: Uses title (e.g., "Project Name" -> projectName) if dataIndex is missing
    const rawKey =
      col.dataIndex || col.field || col.key || col.accessor || label;
    const placeholderKey = toPlaceholderKey(rawKey);

    if (skipKeys.has(placeholderKey)) continue;
    if (!placeholderKey || seen.has(placeholderKey)) continue;

    seen.add(placeholderKey);
    visibleColumns.push({
      key: placeholderKey,
      label: label || placeholderKey,
    });
  }

  if (!visibleColumns.length) return;

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing: Record<string, { key: string; label: string }[]> = raw
      ? JSON.parse(raw)
      : {};
    if (
      JSON.stringify(existing[modelName]) !== JSON.stringify(visibleColumns)
    ) {
      existing[modelName] = visibleColumns;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
    }
  } catch (e) {
    console.warn("[EmailTemplate] Failed to register columns:", e);
  }
};

export const getModelColumns = (
  modelName: string,
): { key: string; label: string }[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing: Record<string, { key: string; label: string }[]> = raw
      ? JSON.parse(raw)
      : {};
    return existing[modelName] || [];
  } catch (e) {
    return [];
  }
};

export const getRegisteredModels = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const existing: Record<string, any> = raw ? JSON.parse(raw) : {};
    return Object.keys(existing);
  } catch (e) {
    return [];
  }
};

export const autoRegisterCurrentPageColumns = (
  columns: any[],
): string | null => {
  if (typeof window === "undefined" || typeof document === "undefined")
    return null;
  if (!Array.isArray(columns) || !columns.length) return null;

  const headingSelectors = [
    ".headername",
    "h1",
    "h2.ant-typography",
    ".ant-page-header-heading-title",
  ];
  for (const selector of headingSelectors) {
    const el = document.querySelector(selector);
    const text = el?.textContent?.trim();
    if (text && text.length > 1 && text.length < 50) {
      registerModelColumns(text, columns);
      return text;
    }
  }

  const pathParts = window.location.pathname.split("/").filter(Boolean);
  const lastPart = pathParts[pathParts.length - 1] || "";
  if (lastPart) {
    const modelName = lastPart
      .replace(/-/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
    registerModelColumns(modelName, columns);
    return modelName;
  }

  return null;
};

export const initializeDynamicModels = (
  modelDefinitions: Record<string, any[]>,
): void => {
  if (typeof window === "undefined") return;
  Object.entries(modelDefinitions).forEach(([modelName, columns]) => {
    registerModelColumns(modelName, columns);
  });
};
